import {Component, OnInit} from "@angular/core";
import { AuthService } from "@app/auth/services/auth.service";
import { FormGroup, Validators, FormControl } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { environment } from "@environments/environment";
import { UserService } from "@app/shared/services/user.service";
import { StoreService } from "@app/shared/services/store.service";

@Component({
  selector: "app-registration",
  templateUrl: "./register.component.html",
  styleUrls: ["./register.component.scss"],
})
export class RegisterComponent implements OnInit {
  MINIMUM_PASSWORD_LENGTH = 8;
  error: string | undefined;
  signupForm: FormGroup;
  moreInfo: boolean = true;
  emailSent: boolean = false;
  signupAttempts = 0;
  submittedForm = false;
  loading = false;
  showLoadingAnimation = false;
  isProd = environment.production;

  upgrading: boolean;
  upgradePlan: any;
  upgradablePlans = {
    'starter': { id: 'springbot-starter', name: 'Springbot Starter'},
    'growth': { id: 'springbot-growth', name: 'Springbot Growth'},
  }

  constructor(
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private userService: UserService,
    private storeService: StoreService,
  ) {}

  ngOnInit() {
    this.createForm();

    this.route.queryParams.subscribe(params => {
      if (!!params['plan'] && this.upgradablePlans[params['plan']]) {
        this.upgrading = true;
        this.upgradePlan = params['plan'];
      }
    });
  }

  createForm(): void {
    this.signupForm = new FormGroup({
      email: new FormControl(
        "",
        [Validators.required, Validators.maxLength(100), Validators.email]
      ),
      password: new FormControl(
        "", [Validators.required, Validators.minLength(this.MINIMUM_PASSWORD_LENGTH)]
      ),
      website: new FormControl({
        value: "", disabled: false,
      }, [Validators.required, Validators.maxLength(255),
        Validators.pattern(/^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/)
      ]),
      recaptchaReactive: new FormControl(null, [Validators.required]),
      agreeToTerms: new FormControl(false, [Validators.requiredTrue]),
    });
    if (!this.isProd) {
      this.recaptchaReactive.clearValidators();
    }
  }

  get email(): FormControl {
    return this.signupForm.get("email") as FormControl;
  }

  get recaptchaReactive(): FormControl {
    return this.signupForm.get("recaptchaReactive") as FormControl;
  }

  get password() {
    return this.signupForm.get("password");
  }

  get website() {
    return this.signupForm.get("website");
  }

  get agreeToTerms() {
    return this.signupForm.get("agreeToTerms");
  }

  changeEmail() {
    this.moreInfo = true;
    this.emailSent = false;
    this.submittedForm = false;
  }

  resendEmail() {
    this.emailSent = true;
    this.onSubmit();
  }

  onSubmit() {
    this.submittedForm = true;
    this.signupForm.markAllAsTouched();
    this.website.setValue(this.formatStoreUrl(this.website.value));
    this.error = "";

    this.userService.clearUser();
    this.storeService.clearStore();

    if (this.signupForm.valid) {
      this.loading = true;
      setTimeout(() => {
        this.showLoadingAnimation = true;
      }, 500);
      const params = {
        email: this.email.value,
        store_url: this.website.value,
        password: this.password.value,
        password_confirmation: this.password.value
      };
      this.authService
      .register(params)
      .subscribe({
        next: this.handleRegistrationResponse.bind(this),
        error: this.handleRegistrationError.bind(this)
     });
    }
  }

  private formatStoreUrl(url: string): string {
    try {
      // Check if the URL starts with a protocol (http or https)
      const regex = /^https?:\/\//i;
      if (!regex.test(url)) {
        url = `https://${url}`; // Prepend "https://" if no protocol
      }

      // Create a URL object and extract protocol and hostname
      const urlObject = new URL(url);
      return `${urlObject.protocol}//${urlObject.hostname}`;

    } catch (error) {
      console.error('Invalid URL');
      return url; // Return the original URL if it's invalid, or handle error as you see fit
    }
  }

  private handleRegistrationResponse(response) {
    if (response.ok && this.upgrading) {
      this.router.navigateByUrl('/upgrade_billing?plan=' + this.upgradePlan);
    } else if (response.ok) {
      window.location.href = '/';
    } else {
      this.loading = false;
      this.showLoadingAnimation = false;
      if (response.error?.error === "email already exists") {
        if (this.signupAttempts >= 3) {
          this.router.navigateByUrl("/login");
        } else {
          this.error = "Are you trying to <a href='/login'>sign in</a>?";
          this.signupAttempts++;
        }
      } else if (response.status === 403) {
        this.error = "Registration invalid. Check your store website and try again.";
      } else if (response.status === 429) {
        this.error = "Too many attempts. Try again later.";
      } else if (response.status === 401) {
        this.error = response.error.error;
      } else {
        this.error = "An unknown error has occurred.";
      }
    }
  }

  private handleRegistrationError(response) {
    this.loading = false;
    this.showLoadingAnimation = false;
    this.error = "An unexpected error has occurred."
  }

  private arePlusAndNumberIncluded(email: string) {
    const plusIndex = email.indexOf("+");
    return plusIndex > 0 && !Number.isNaN(parseInt(email[plusIndex + 1], 10));
  }
}
